// src/components/CursorParticles.jsx
import React, { useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const CursorParticles = () => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0, vx: 0, vy: 0 });
  const animationRef = useRef(null);
  const timeRef = useRef(0);
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let width = window.innerWidth;
    let height = window.innerHeight;

    // Particle class with black & white theme
    class Particle {
      constructor(x, y) {
        this.x = x;
        this.y = y;
        this.originalX = x;
        this.originalY = y;
        this.size = 2 + Math.random() * 5;
        this.baseSize = this.size;
        this.speedX = (Math.random() - 0.5) * 0.3;
        this.speedY = (Math.random() - 0.5) * 0.3;
        this.angle = Math.random() * Math.PI * 2;
        this.angleSpeed = 0.005 + Math.random() * 0.015;
        this.radius = 50 + Math.random() * 100;
        this.opacity = 0.1 + Math.random() * 0.3;
        this.shape = Math.floor(Math.random() * 4);
        this.trail = [];
        this.blinkSpeed = 0.02 + Math.random() * 0.03;
        this.blinkPhase = Math.random() * Math.PI * 2;
      }

      update(mouseX, mouseY, mouseVx, mouseVy, time, isDark) {
        const dx = this.x - mouseX;
        const dy = this.y - mouseY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const maxDistance = 200;

        this.trail.unshift({ x: this.x, y: this.y });
        if (this.trail.length > 5) this.trail.pop();

        if (distance < maxDistance) {
          const force = (maxDistance - distance) / maxDistance;
          const angle = Math.atan2(dy, dx);
          const pushX = Math.cos(angle) * force * 2.5;
          const pushY = Math.sin(angle) * force * 2.5;
          this.x += pushX + mouseVx * 0.3;
          this.y += pushY + mouseVy * 0.3;
        } else {
          this.x += (this.originalX - this.x) * 0.02;
          this.y += (this.originalY - this.y) * 0.02;
        }

        this.angle += this.angleSpeed;
        const orbitalX = Math.cos(this.angle + time) * this.radius * 0.2;
        const orbitalY = Math.sin(this.angle * 0.7 + time) * this.radius * 0.2;

        this.x += orbitalX * 0.008;
        this.y += orbitalY * 0.008;
        this.x += Math.sin(time + this.originalX) * 0.05;
        this.y += Math.cos(time * 0.7 + this.originalY) * 0.05;

        const margin = 50;
        if (this.x < margin) this.x = margin;
        if (this.x > width - margin) this.x = width - margin;
        if (this.y < margin) this.y = margin;
        if (this.y > height - margin) this.y = height - margin;

        if (distance < maxDistance) {
          this.size =
            this.baseSize + ((maxDistance - distance) / maxDistance) * 3;
        } else {
          this.size = this.baseSize;
        }

        const blink =
          0.5 + Math.sin(time * this.blinkSpeed + this.blinkPhase) * 0.3;
        let baseOpacity = isDark ? 0.15 : 0.08;
        if (distance < maxDistance) {
          baseOpacity = isDark ? 0.4 : 0.25;
        }
        this.currentOpacity = Math.min(baseOpacity * blink, 0.5);
      }

      draw(ctx, time, isDark) {
        const mainColor = isDark
          ? `rgba(255, 255, 255, ${this.currentOpacity})`
          : `rgba(0, 0, 0, ${this.currentOpacity})`;
        const glowColor = isDark
          ? `rgba(255, 255, 255, ${this.currentOpacity * 0.5})`
          : `rgba(0, 0, 0, ${this.currentOpacity * 0.5})`;
        const borderColor = isDark
          ? `rgba(255, 255, 255, ${this.currentOpacity * 0.8})`
          : `rgba(0, 0, 0, ${this.currentOpacity * 0.8})`;

        for (let i = 0; i < this.trail.length; i++) {
          const trailOpacity =
            this.currentOpacity * (1 - i / this.trail.length) * 0.5;
          ctx.beginPath();
          ctx.arc(
            this.trail[i].x,
            this.trail[i].y,
            this.size * (1 - i / this.trail.length) * 0.7,
            0,
            Math.PI * 2,
          );
          ctx.fillStyle = isDark
            ? `rgba(255, 255, 255, ${trailOpacity})`
            : `rgba(0, 0, 0, ${trailOpacity})`;
          ctx.fill();
        }

        ctx.save();
        ctx.shadowBlur = this.size * 1.5;
        ctx.shadowColor = glowColor;

        switch (this.shape) {
          case 0:
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
            ctx.fillStyle = mainColor;
            ctx.fill();
            ctx.strokeStyle = borderColor;
            ctx.lineWidth = 0.5;
            ctx.stroke();
            break;
          case 1:
            ctx.fillStyle = mainColor;
            ctx.fillRect(
              this.x - this.size,
              this.y - this.size,
              this.size * 2,
              this.size * 2,
            );
            ctx.strokeStyle = borderColor;
            ctx.lineWidth = 0.5;
            ctx.strokeRect(
              this.x - this.size,
              this.y - this.size,
              this.size * 2,
              this.size * 2,
            );
            break;
          case 2:
            ctx.beginPath();
            const height = this.size * 1.732;
            ctx.moveTo(this.x, this.y - this.size);
            ctx.lineTo(this.x + this.size, this.y + this.size * 0.5);
            ctx.lineTo(this.x - this.size, this.y + this.size * 0.5);
            ctx.closePath();
            ctx.fillStyle = mainColor;
            ctx.fill();
            ctx.strokeStyle = borderColor;
            ctx.lineWidth = 0.5;
            ctx.stroke();
            break;
          case 3:
            ctx.beginPath();
            ctx.moveTo(this.x, this.y - this.size);
            ctx.lineTo(this.x + this.size, this.y);
            ctx.lineTo(this.x, this.y + this.size);
            ctx.lineTo(this.x - this.size, this.y);
            ctx.closePath();
            ctx.fillStyle = mainColor;
            ctx.fill();
            ctx.strokeStyle = borderColor;
            ctx.lineWidth = 0.5;
            ctx.stroke();
            break;
        }

        ctx.restore();
      }
    }

    const initParticles = () => {
      const particles = [];
      const particleCount = Math.min(120, Math.floor((width * height) / 10000));
      for (let i = 0; i < particleCount; i++) {
        const x = Math.random() * width;
        const y = Math.random() * height;
        particles.push(new Particle(x, y));
      }
      particlesRef.current = particles;
    };

    let lastMouseX = width / 2;
    let lastMouseY = height / 2;
    let lastMouseTime = Date.now();

    const handleMouseMove = e => {
      const now = Date.now();
      const dt = Math.max(1, now - lastMouseTime);
      mouseRef.current.vx = ((e.clientX - lastMouseX) / dt) * 16;
      mouseRef.current.vy = ((e.clientY - lastMouseY) / dt) * 16;
      mouseRef.current.vx = Math.min(Math.max(mouseRef.current.vx, -10), 10);
      mouseRef.current.vy = Math.min(Math.max(mouseRef.current.vy, -10), 10);
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
      lastMouseX = e.clientX;
      lastMouseY = e.clientY;
      lastMouseTime = now;
    };

    const handleMouseLeave = () => {
      mouseRef.current.vx = 0;
      mouseRef.current.vy = 0;
    };

    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      initParticles();
    };

    const animate = () => {
      if (!ctx) return;
      const time = performance.now() / 1000;
      timeRef.current = time;
      const isDark = resolvedTheme === 'dark';

      ctx.fillStyle = isDark
        ? 'rgba(10, 10, 10, 0.08)'
        : 'rgba(255, 255, 255, 0.08)';
      ctx.fillRect(0, 0, width, height);

      particlesRef.current.forEach(particle => {
        particle.update(
          mouseRef.current.x,
          mouseRef.current.y,
          mouseRef.current.vx,
          mouseRef.current.vy,
          time,
          isDark,
        );
        particle.draw(ctx, time, isDark);
      });

      for (let i = 0; i < particlesRef.current.length; i++) {
        for (let j = i + 1; j < particlesRef.current.length; j++) {
          const dx = particlesRef.current[i].x - particlesRef.current[j].x;
          const dy = particlesRef.current[i].y - particlesRef.current[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 80) {
            ctx.beginPath();
            ctx.moveTo(particlesRef.current[i].x, particlesRef.current[i].y);
            ctx.lineTo(particlesRef.current[j].x, particlesRef.current[j].y);
            const opacity = 0.02 * (1 - distance / 80);
            ctx.strokeStyle = isDark
              ? `rgba(255, 255, 255, ${opacity})`
              : `rgba(0, 0, 0, ${opacity})`;
            ctx.stroke();
          }
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    const setup = () => {
      canvas.width = width;
      canvas.height = height;
      initParticles();
      mouseRef.current.x = width / 2;
      mouseRef.current.y = height / 2;
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseleave', handleMouseLeave);
      window.addEventListener('resize', handleResize);
      animate();
    };

    setup();

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [resolvedTheme]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute top-0 left-0 w-full h-full pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
};

export default CursorParticles;
